import { QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdminMemberDTO,
  EditProjectInput,
  ImportProjectsResult,
  NewProjectInput,
  AdminTeamDTO,
  AskAnswerDTO,
  AskPresetKey,
  AuditEntryDTO,
  DashboardDTO,
  InboxDTO,
  LeaveDTO,
  MeDTO,
  McpTokenDTO,
  OAuthGrantDTO,
  MemberDTO,
  NotificationDTO,
  OffboardPreviewDTO,
  ProjectDetailDTO,
  ProjectStatus,
  RadarProjectDTO,
  ReportPageDTO,
  ResolutionKind,
  Role,
  Settings,
  PersonDTO,
  SettingsDTO,
  TeamStatusDTO,
  WeeklyDTO,
  WeeklySection,
  ProgramCharter,
  ProgramDTO,
  ProgramInput,
  ProgramPlanItem,
  ProgramRag,
  ProgramReportDTO,
  ProgramSummaryDTO,
} from '@wd/shared';
import { api, ApiError, del, patch, post, put } from './api';
import { syncServerClock } from './clock';

/** 只重试网络错误和服务器错误；内容校验不通过（4xx）重试也没用，直接把原因告诉用户 */
const retryable = (max: number) => (failureCount: number, error: unknown) =>
  failureCount < max && (!(error instanceof ApiError) || error.status === 0 || error.status >= 500);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: retryable(1), refetchOnWindowFocus: true, staleTime: 5_000 },
    // 离线时先排队，联网后自动重新提交（设计稿 20b：失败不丢内容）
    mutations: { networkMode: 'offlineFirst', retry: retryable(2) },
  },
});

export type ProjectWithMine = RadarProjectDTO;

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const me = await api<MeDTO>('/me');
      syncServerClock(me.serverNow);
      return me;
    },
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useReport(date: string, memberId?: string) {
  return useQuery({
    queryKey: ['report', date, memberId ?? 'me'],
    queryFn: () => api<ReportPageDTO>(`/reports/${date}${memberId ? `?member=${memberId}` : ''}`),
    // 草稿期间轮询，接住异步生成的 AI 追问
    refetchInterval: (q) => (q.state.data?.status === 'draft' ? 8_000 : false),
  });
}

export function useInbox() {
  return useQuery({ queryKey: ['inbox'], queryFn: () => api<InboxDTO>('/inbox'), refetchInterval: 60_000 });
}

export function useProjects() {
  return useQuery({ queryKey: ['projects'], queryFn: () => api<RadarProjectDTO[]>('/projects'), staleTime: 30_000 });
}

export function useProject(id: string | undefined) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: () => api<ProjectDetailDTO>(`/projects/${id}`),
    enabled: !!id,
    refetchInterval: 60_000,
  });
}

export interface ProjectPatch {
  status?: ProjectStatus;
  dueDate?: string | null;
  dueReason?: string | null;
  nextStep?: { text: string; date: string | null; assigneeId: string | null } | null;
  phase?: string | null;
  phaseIndex?: number | null;
  phaseCount?: number | null;
  description?: string | null;
  customer?: string | null;
}

export function useProjectActions(id: string | undefined) {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(['project', id ?? ''], ['projects'], ['report'], ['inbox']);
  return {
    update: useMutation({ mutationFn: (body: ProjectPatch) => patch(`/projects/${id}`, body), onSuccess: refresh }),
    comment: useMutation({
      mutationFn: (v: { text: string; attachmentIds: string[] }) => post<{ id: string }>(`/projects/${id}/comments`, v),
      retry: 0,
      onSuccess: refresh,
    }),
    /** 名称、负责人、团队、参与人 */
    editBasics: useMutation({
      mutationFn: (body: EditProjectInput) => put(`/projects/${id}/basics`, body),
      retry: 0,
      onSuccess: () => invalidate(['project', id ?? ''], ['projects'], ['report'], ['inbox'], ['me'], ['members'], ['person']),
    }),
  };
}

/** 新建项目。不重试：网络抖动时重放会建出两个项目 */
export function useCreateProject() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (body: NewProjectInput) => post<{ id: string; code: string }>('/projects', body),
    retry: 0,
    onSuccess: () => invalidate(['projects'], ['report'], ['me'], ['members'], ['person']),
  });
}

/** 管理员批量导入项目。不重试：重放会把成功的行再导一遍（同名会被跳过，但结果看着乱） */
export function useImportProjects() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (csv: string) => post<ImportProjectsResult>('/admin/projects/import', { csv }),
    retry: 0,
    onSuccess: () => invalidate(['projects'], ['report'], ['me'], ['members'], ['person']),
  });
}

export function useMembers() {
  return useQuery({ queryKey: ['members'], queryFn: () => api<MemberDTO[]>('/members'), staleTime: 300_000 });
}

export function useLeaves() {
  return useQuery({ queryKey: ['leaves'], queryFn: () => api<LeaveDTO[]>('/leaves') });
}

export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: () => api<Settings>('/settings'), staleTime: 300_000 });
}

/** 写操作后刷新相关数据 */
function useInvalidate() {
  const qc = useQueryClient();
  return (...keys: string[][]) => Promise.all(keys.map((k) => qc.invalidateQueries({ queryKey: k })));
}

export function useReportActions() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(['report'], ['me'], ['inbox']);
  return {
    updateItem: useMutation({
      mutationFn: (v: { id: string; text?: string; noProgress?: boolean }) => patch(`/report-items/${v.id}`, { text: v.text, noProgress: v.noProgress }),
      onSuccess: refresh,
    }),
    addItem: useMutation({
      mutationFn: (v: { reportId: string; projectId: string }) => post<{ id: string }>(`/reports/${v.reportId}/items`, { projectId: v.projectId }),
      onSuccess: refresh,
    }),
    setNote: useMutation({
      mutationFn: (v: { reportId: string; note: string }) => patch(`/reports/${v.reportId}/note`, { note: v.note }),
      onSuccess: refresh,
    }),
    addPlan: useMutation({
      mutationFn: (v: { reportId: string; projectId: string | null; text: string }) =>
        post<{ id: string }>(`/reports/${v.reportId}/plans`, { projectId: v.projectId, text: v.text }),
      onSuccess: refresh,
    }),
    updatePlan: useMutation({
      mutationFn: (v: { id: string; text?: string; checked?: boolean; projectId?: string | null }) =>
        patch(`/plans/${v.id}`, { text: v.text, checked: v.checked, projectId: v.projectId }),
      onSuccess: refresh,
    }),
    deletePlan: useMutation({ mutationFn: (id: string) => del(`/plans/${id}`), onSuccess: refresh }),
    submit: useMutation({
      mutationFn: (reportId: string) =>
        post<{ status: 'submitted'; submittedAt: string; late: boolean } | { status: 'follow_up'; followUpId: string }>(`/reports/${reportId}/submit`),
      onSuccess: refresh,
    }),
    retract: useMutation({ mutationFn: (reportId: string) => post(`/reports/${reportId}/retract`), onSuccess: refresh }),
    answer: useMutation({
      mutationFn: (v: { id: string; answer: string }) => post(`/follow-ups/${v.id}/answer`, { answer: v.answer }),
      onSuccess: refresh,
    }),
    skip: useMutation({ mutationFn: (id: string) => post(`/follow-ups/${id}/skip`), onSuccess: refresh }),
    feedback: useMutation({
      mutationFn: (v: { itemId: string; reason: string; text: string | null }) => post(`/report-items/${v.itemId}/feedback`, { reason: v.reason, text: v.text }),
    }),
    requestExit: useMutation({
      mutationFn: (v: { projectId: string; reason: string; handoverToId: string | null }) => post(`/projects/${v.projectId}/exit`, v),
      onSuccess: refresh,
    }),
  };
}

export function useLeaveActions() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(['leaves'], ['report'], ['me']);
  return {
    create: useMutation({
      mutationFn: (v: { type: 'leave' | 'trip' | 'rest'; from: string; to: string; note: string | null; notifyOwners: boolean }) => post('/leaves', v),
      onSuccess: refresh,
    }),
    cancel: useMutation({ mutationFn: (id: string) => del(`/leaves/${id}`), onSuccess: refresh }),
  };
}

export function useInboxActions() {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(['inbox'], ['me']);
  return {
    ask: useMutation({
      mutationFn: (v: { id: string; toId: string; ccIds: string[]; question: string; requeue: boolean }) => post(`/anomalies/${v.id}/ask`, v),
      onSuccess: refresh,
    }),
    assign: useMutation({
      mutationFn: (v: { id: string; assigneeId: string; ask: string; dueAt: string }) => post(`/anomalies/${v.id}/assign`, v),
      onSuccess: refresh,
    }),
    resolve: useMutation({
      mutationFn: (v: { id: string; kind: ResolutionKind; note: string | null; recheckAt: string | null }) => post(`/anomalies/${v.id}/resolve`, v),
      onSuccess: refresh,
    }),
    complete: useMutation({ mutationFn: (v: { id: string; reply: string }) => post(`/assignments/${v.id}/complete`, v), onSuccess: refresh }),
    giveBack: useMutation({ mutationFn: (v: { id: string; reason: string }) => post(`/assignments/${v.id}/return`, v), onSuccess: refresh }),
    transfer: useMutation({
      mutationFn: (v: { id: string; toId: string; note: string | null }) => post(`/assignments/${v.id}/transfer`, v),
      onSuccess: refresh,
    }),
    approveExit: useMutation({
      mutationFn: (v: { id: string; planHandoverToId: string | null }) => post(`/exit-requests/${v.id}/approve`, v),
      onSuccess: refresh,
    }),
    rejectExit: useMutation({ mutationFn: (v: { id: string; reason: string }) => post(`/exit-requests/${v.id}/reject`, v), onSuccess: refresh }),
    answer: useMutation({
      mutationFn: (v: { id: string; answer: string }) => post(`/follow-ups/${v.id}/answer`, { answer: v.answer }),
      onSuccess: () => invalidate(['inbox'], ['me'], ['report']),
    }),
  };
}

export const fetchAskDraft = (id: string) =>
  api<{ toId: string; ccIds: string[]; question: string; alternatives: string[] }>(`/anomalies/${id}/ask-draft`);

export interface AssignCandidate {
  id: string;
  name: string;
  initial: string;
  teamName: string | null;
  note: string | null;
  load: number;
  recommended: boolean;
}

export const fetchAssignCandidates = (id: string) => api<AssignCandidate[]>(`/anomalies/${id}/assign-candidates`);

// ---------------------------------------------------------------------------
// 周报（设计稿 14a）
// ---------------------------------------------------------------------------

export function useWeekly(week: string | undefined, scope: string | null) {
  const qs = new URLSearchParams();
  if (week) qs.set('week', week);
  if (scope) qs.set('scope', scope);
  return useQuery({
    queryKey: ['weekly', week ?? 'current', scope ?? 'default'],
    queryFn: () => api<WeeklyDTO>(`/weekly?${qs}`),
    retry: false,
  });
}

export function useWeeklyActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['weekly'] });
  return {
    draft: useMutation({
      mutationFn: (v: { week: string; scope: string }) => post<WeeklyDTO>('/weekly/draft', v),
      // AI 起草可能要十几秒，失败不自动重试
      retry: 0,
      onSuccess: refresh,
    }),
    edit: useMutation({ mutationFn: (v: { id: string; itemId: string; text: string }) => patch(`/weekly/${v.id}/items/${v.itemId}`, { text: v.text }), onSuccess: refresh }),
    remove: useMutation({ mutationFn: (v: { id: string; itemId: string }) => del(`/weekly/${v.id}/items/${v.itemId}`), onSuccess: refresh }),
    add: useMutation({
      mutationFn: (v: { id: string; section: WeeklySection; text: string }) => post<{ id: string }>(`/weekly/${v.id}/items`, { section: v.section, text: v.text }),
      onSuccess: refresh,
    }),
    send: useMutation({ mutationFn: (id: string) => post<{ recipients: number }>(`/weekly/${id}/send`), retry: 0, onSuccess: refresh }),
  };
}

// ---------------------------------------------------------------------------
// 设置（设计稿 14c、22c–e）
// ---------------------------------------------------------------------------

export function useSettingsFull() {
  return useQuery({ queryKey: ['settings-full'], queryFn: () => api<SettingsDTO>('/settings/full') });
}

export function useSettingsActions() {
  const qc = useQueryClient();
  const apply = (dto: SettingsDTO) => {
    qc.setQueryData(['settings-full'], dto);
    void qc.invalidateQueries({ queryKey: ['settings'] });
    void qc.invalidateQueries({ queryKey: ['me'] });
  };
  return {
    update: useMutation({ mutationFn: (p: Partial<Settings>) => api<SettingsDTO>('/settings', { method: 'PUT', body: p }), onSuccess: apply }),
    discard: useMutation({ mutationFn: () => post<SettingsDTO>('/settings/discard-pending'), onSuccess: apply }),
    rename: useMutation({ mutationFn: (name: string) => api<SettingsDTO>('/workspace', { method: 'PUT', body: { name } }), onSuccess: apply }),
  };
}

export function useAdminDirectory(enabled = true) {
  return useQuery({
    queryKey: ['admin-directory'],
    queryFn: () => api<{ members: AdminMemberDTO[]; teams: AdminTeamDTO[] }>('/admin/directory'),
    enabled,
    retry: false,
  });
}

export interface MemberInput {
  name: string;
  email: string | null;
  teamId: string | null;
  role: Role;
  managerId: string | null;
  title?: string | null;
  responsibility?: string | null;
}

export function useAdminActions() {
  const qc = useQueryClient();
  const refresh = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['admin-directory'] }),
      qc.invalidateQueries({ queryKey: ['members'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
      qc.invalidateQueries({ queryKey: ['audit'] }),
    ]);
  return {
    invite: useMutation({ mutationFn: (v: MemberInput) => post<{ id: string; mailed: boolean }>('/admin/members', v), retry: 0, onSuccess: refresh }),
    update: useMutation({ mutationFn: (v: { id: string } & Partial<MemberInput>) => patch(`/admin/members/${v.id}`, { ...v, id: undefined }), onSuccess: refresh }),
    reinvite: useMutation({ mutationFn: (id: string) => post<{ mailed: boolean }>(`/admin/members/${id}/reinvite`), retry: 0, onSuccess: refresh }),
    revokeMcp: useMutation({ mutationFn: (id: string) => del(`/admin/members/${id}/mcp-token`), retry: 0, onSuccess: refresh }),
    importCsv: useMutation({
      mutationFn: (csv: string) => post<{ created: number; skipped: { line: number; reason: string }[]; unmailed: string[] }>('/admin/members/import', { csv }),
      retry: 0,
      onSuccess: refresh,
    }),
    offboard: useMutation({
      mutationFn: (v: { id: string; lastDay: string; handovers: { projectId: string; toId: string }[] }) =>
        post(`/admin/members/${v.id}/offboard`, { lastDay: v.lastDay, handovers: v.handovers }),
      retry: 0,
      onSuccess: refresh,
    }),
    cancelOffboard: useMutation({ mutationFn: (id: string) => post(`/admin/members/${id}/offboard/cancel`), onSuccess: refresh }),
    reassign: useMutation({
      mutationFn: (v: { memberId: string; projectId: string; toId: string }) => post(`/admin/members/${v.memberId}/handovers`, { projectId: v.projectId, toId: v.toId }),
      onSuccess: refresh,
    }),
    createTeam: useMutation({ mutationFn: (name: string) => post<{ id: string }>('/admin/teams', { name }), onSuccess: refresh }),
    renameTeam: useMutation({ mutationFn: (v: { id: string; name: string }) => patch(`/admin/teams/${v.id}`, { name: v.name }), onSuccess: refresh }),
    deleteTeam: useMutation({ mutationFn: (id: string) => del(`/admin/teams/${id}`), onSuccess: refresh }),
  };
}

export const fetchOffboardPreview = (id: string) => api<OffboardPreviewDTO>(`/admin/members/${id}/offboard-preview`);

export function useAuditLog(enabled = true) {
  return useInfiniteQuery({
    queryKey: ['audit'],
    queryFn: ({ pageParam }) => api<{ entries: AuditEntryDTO[]; next: string | null }>(`/admin/audit${pageParam ? `?before=${encodeURIComponent(pageParam)}` : ''}`),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next,
    enabled,
  });
}

export function useHandoverActions() {
  const qc = useQueryClient();
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ['inbox'] }), qc.invalidateQueries({ queryKey: ['me'] }), qc.invalidateQueries({ queryKey: ['projects'] })]);
  return {
    accept: useMutation({ mutationFn: (id: string) => post(`/handovers/${id}/accept`), onSuccess: refresh }),
    decline: useMutation({ mutationFn: (v: { id: string; reason: string }) => post(`/handovers/${v.id}/decline`, { reason: v.reason }), onSuccess: refresh }),
  };
}

// ---------------------------------------------------------------------------
// 团队状态 · 建议关注（设计稿 9a、19b、19c）
// ---------------------------------------------------------------------------

export function useTeamStatus(week: string | undefined) {
  return useQuery({
    queryKey: ['team', week ?? 'current'],
    queryFn: () => api<TeamStatusDTO>(`/team${week ? `?week=${week}` : ''}`),
    retry: false,
  });
}

export function useInsightActions() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['team'] });
  return {
    regenerate: useMutation({ mutationFn: (week: string) => post<TeamStatusDTO>('/team/refresh', { week }), retry: 0, onSuccess: refresh }),
    talk: useMutation({
      mutationFn: (v: { id: string; date: string; method: '1:1' | 'phone' | 'wechat'; note: string | null; nextWeek: 'keep_watching' | 'mute_4_weeks' }) =>
        post(`/insights/${v.id}/talk`, { date: v.date, method: v.method, note: v.note, nextWeek: v.nextWeek }),
      onSuccess: refresh,
    }),
    recognize: useMutation({ mutationFn: (v: { id: string; thanks: string | null }) => post(`/insights/${v.id}/recognize`, { thanks: v.thanks }), retry: 0, onSuccess: refresh }),
    schedule: useMutation({ mutationFn: (v: { id: string; at: string; text: string }) => post(`/insights/${v.id}/schedule`, { at: v.at, text: v.text }), retry: 0, onSuccess: refresh }),
    status: useMutation({ mutationFn: (v: { id: string; status: 'open' | 'dismissed' }) => post(`/insights/${v.id}/status`, { status: v.status }), onSuccess: refresh }),
    feedback: useMutation({ mutationFn: (v: { id: string; reason: string; text: string | null }) => post(`/insights/${v.id}/feedback`, { reason: v.reason, text: v.text }), onSuccess: refresh }),
  };
}

export function useMessageRead() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => post(`/messages/${id}/read`), onSuccess: () => qc.invalidateQueries({ queryKey: ['inbox'] }) });
}

/** 问 AI。不重试：每次都要调模型，失败了让用户自己决定要不要再问 */
export function useAsk() {
  return useMutation({
    mutationFn: (v: { question: string; preset?: AskPresetKey | null; projectId?: string | null }) =>
      post<AskAnswerDTO>('/ask', { question: v.question, preset: v.preset ?? null, projectId: v.projectId ?? null }),
    retry: 0,
    networkMode: 'online',
  });
}

/** 本地智能体（MCP）令牌 */
export function useMcpToken() {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['mcp-token'] });
  return {
    info: useQuery({ queryKey: ['mcp-token'], queryFn: () => api<McpTokenDTO>('/me/mcp-token') }),
    issue: useMutation({ mutationFn: () => post<{ token: string }>('/me/mcp-token'), retry: 0, onSuccess: refresh }),
    revoke: useMutation({ mutationFn: () => del('/me/mcp-token'), retry: 0, onSuccess: refresh }),
  };
}

/** 用连接器接入的 Claude（手机、网页版） */
export function useOAuthGrants() {
  const qc = useQueryClient();
  return {
    list: useQuery({ queryKey: ['oauth-grants'], queryFn: () => api<OAuthGrantDTO[]>('/me/oauth-grants') }),
    revoke: useMutation({ mutationFn: (id: string) => del(`/me/oauth-grants/${id}`), retry: 0, onSuccess: () => qc.invalidateQueries({ queryKey: ['oauth-grants'] }) }),
  };
}

export function useDashboard() {
  return useQuery({ queryKey: ['dashboard'], queryFn: () => api<DashboardDTO>('/dashboard'), refetchInterval: 120_000 });
}

export function useNotifications() {
  return useQuery({ queryKey: ['notifications'], queryFn: () => api<NotificationDTO[]>('/notifications'), refetchInterval: 60_000 });
}

/** 标为已读：不传 ids 就是全部 */
export function useMarkNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) => post('/notifications/read', ids ? { ids } : {}),
    onSuccess: () => Promise.all([qc.invalidateQueries({ queryKey: ['notifications'] }), qc.invalidateQueries({ queryKey: ['me'] })]),
  });
}

export function usePerson(id: string | undefined) {
  return useQuery({ queryKey: ['person', id], queryFn: () => api<PersonDTO>(`/people/${id}`), enabled: !!id, retry: false });
}


// ---------------------------------------------------------------------------
// 项目集与进度汇报
// ---------------------------------------------------------------------------

export function usePrograms() {
  return useQuery({ queryKey: ['programs'], queryFn: () => api<ProgramSummaryDTO[]>('/programs'), staleTime: 30_000 });
}

export function useProgram(id: string | undefined) {
  return useQuery({ queryKey: ['program', id], queryFn: () => api<ProgramDTO>(`/programs/${id}`), enabled: !!id, retry: false });
}

export function useProgramReport(id: string | null | undefined) {
  return useQuery({ queryKey: ['program-report', id], queryFn: () => api<ProgramReportDTO>(`/program-reports/${id}`), enabled: !!id, retry: false });
}

export function useCreateProgram() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (v: ProgramInput) => post<{ id: string; code: string }>('/programs', v), retry: 0, onSuccess: () => invalidate(['programs'], ['me']) });
}

export interface ProgramPatch extends Partial<ProgramInput> {
  charter?: Partial<ProgramCharter>;
  status?: 'active' | 'closed';
}

export interface ReportPatch {
  rag?: ProgramRag;
  summary?: string;
  risks?: string;
  asks?: string;
  rows?: { projectId: string; progress?: string; delayNote?: string }[];
  lastPeriod?: ProgramPlanItem[];
  nextPeriod?: ProgramPlanItem[];
}

export function useProgramActions(id: string | undefined) {
  const invalidate = useInvalidate();
  const refresh = () => invalidate(['program', id ?? ''], ['programs'], ['program-report'], ['projects']);
  return {
    update: useMutation({ mutationFn: (v: ProgramPatch) => patch(`/programs/${id}`, v), onSuccess: refresh }),
    addTasks: useMutation({
      mutationFn: (items: { projectId: string; section: string | null }[]) => post<{ added: number }>(`/programs/${id}/tasks`, { items }),
      onSuccess: refresh,
    }),
    createTask: useMutation({
      mutationFn: (v: { name: string; ownerId: string; participantIds: string[]; section: string | null; startDate: string | null; dueDate: string | null }) =>
        post<{ projectId: string; code: string }>(`/programs/${id}/tasks/new`, v),
      retry: 0,
      onSuccess: refresh,
    }),
    updateTask: useMutation({
      mutationFn: (v: { projectId: string; section?: string | null; baselineStart?: string | null; baselineEnd?: string | null; move?: 'up' | 'down' }) => {
        const { projectId, ...rest } = v;
        return patch(`/programs/${id}/tasks/${projectId}`, rest);
      },
      onSuccess: refresh,
    }),
    removeTask: useMutation({ mutationFn: (projectId: string) => del(`/programs/${id}/tasks/${projectId}`), onSuccess: refresh }),
    renameSection: useMutation({ mutationFn: (v: { from: string; to: string }) => post(`/programs/${id}/sections/rename`, v), onSuccess: refresh }),
    /** 关键任务的截止日、状态：走项目本身的接口 */
    updateProject: useMutation({ mutationFn: (v: ProjectPatch & { projectId: string }) => {
      const { projectId, ...rest } = v;
      return patch(`/projects/${projectId}`, rest);
    }, onSuccess: refresh }),
    draft: useMutation({ mutationFn: () => post<ProgramReportDTO>(`/programs/${id}/reports/draft`), retry: 0, onSuccess: refresh }),
  };
}

export function useReportEdit(reportId: string | null | undefined) {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  return {
    edit: useMutation({
      mutationFn: (v: ReportPatch) => patch(`/program-reports/${reportId}`, v),
      // 先改本地缓存，表格里不闪
      onMutate: (v) => {
        qc.setQueryData<ProgramReportDTO>(['program-report', reportId], (old) => {
          if (!old) return old;
          const rows = v.rows
            ? old.rows.map((r) => {
                const hit = v.rows!.find((x) => x.projectId === r.projectId);
                return hit ? { ...r, ...hit, edited: true } : r;
              })
            : old.rows;
          return { ...old, ...v, rows };
        });
      },
      onSettled: () => invalidate(['program-report', reportId ?? '']),
    }),
    send: useMutation({ mutationFn: () => post<{ recipients: number }>(`/program-reports/${reportId}/send`), retry: 0, onSuccess: () => invalidate(['program-report'], ['program'], ['programs']) }),
    remove: useMutation({ mutationFn: () => del(`/program-reports/${reportId}`), onSuccess: () => invalidate(['program-report'], ['program'], ['programs']) }),
  };
}
